import type { ReactElement, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { EmptyState, ErrorState, Spinner, Table } from '../../components'
import type { TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import {
  CONFIG_BM_HOMOLOGATIONS_PATH,
  CONFIG_CHANGES_PATH,
  CONFIG_MACHINE_GROUPS_PATH,
  CONFIG_PLAN_MACHINES_PATH,
  CONFIG_QUALITY_RULES_PATH,
  CONFIG_SETTINGS_PATH,
  CONFIG_SPECIAL_SCHEDULE_PATH,
} from '../../lib/routes'
import { trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, adminStyles as styles, formatDateTime } from '../admin/AdminPage'
import { ENTITY_LABEL } from '../admin/ChangesTable'

type ConfigurationStatus = RouterOutputs['configuration']['getStatus']
type Counts = ConfigurationStatus['counts']
type RequiredTable = ConfigurationStatus['warnings']['missingForPlanning'][number]
type SheetImport = ConfigurationStatus['sheetImports'][number]

/** What each count is, and where it is edited. */
const COUNTS: Array<{ key: keyof Counts; label: string; path: string }> = [
  { key: 'machineGroups', label: 'Máquinas con grupo', path: CONFIG_MACHINE_GROUPS_PATH },
  { key: 'planMachines', label: 'Máquinas del plan', path: CONFIG_PLAN_MACHINES_PATH },
  { key: 'qualityRules', label: 'Reglas de calidad', path: CONFIG_QUALITY_RULES_PATH },
  { key: 'specialSchedule', label: 'Recetas de cocción baja', path: CONFIG_SPECIAL_SCHEDULE_PATH },
  { key: 'bmHomologations', label: 'Homologaciones BM', path: CONFIG_BM_HOMOLOGATIONS_PATH },
]

const REQUIRED: Record<RequiredTable, { label: string; path: string }> = {
  machineGroups: { label: 'los grupos de las máquinas', path: CONFIG_MACHINE_GROUPS_PATH },
  planMachines: { label: 'las máquinas del plan', path: CONFIG_PLAN_MACHINES_PATH },
  qualityRules: { label: 'las reglas de calidad', path: CONFIG_QUALITY_RULES_PATH },
}

const ACTION_LABEL: Record<NonNullable<ConfigurationStatus['lastChange']>['action'], string> = {
  create: 'alta',
  update: 'cambio',
  delete: 'baja',
  move: 'cambio de orden',
}

const SHEET_IMPORT_COLUMNS: Array<TableColumn<SheetImport>> = [
  { key: 'when', header: 'Cuándo', render: (sync) => formatDateTime(sync.syncedAt) },
  {
    key: 'counts',
    header: 'Filas cargadas',
    render: (sync) => `Grupo ${sync.counts.workstationPlanningGroups} · Capacidades ${sync.counts.planMachines} · `
      + `Calidad ${sync.counts.qualityRules} · Cocción baja ${sync.counts.specialScheduleRecipes} · `
      + `BM ${sync.counts.bmHomologations}`,
  },
  { key: 'warnings', header: 'Avisos', align: 'right', render: (sync) => sync.warnings.length },
]

/**
 * The home of the planner's configuration: how much of each table there is,
 * the last change, and what needs a look — first of all what keeps the planner
 * from generating a plan at all.
 */
export const ConfigStatusPage = (): ReactElement => {
  const statusQuery = trpc.configuration.getStatus.useQuery()

  if (statusQuery.isPending) {
    return <Spinner size="page" label="Cargando la configuración…" />
  }
  if (statusQuery.isError) {
    return (
      <section className={styles['admin-page']}>
        <AdminPageHeader title="Estado de la configuración" />
        <ErrorState error={statusQuery.error} onRetry={() => void statusQuery.refetch()} />
      </section>
    )
  }

  const { counts, lastChange, warnings, sheetImports, freshness } = statusQuery.data

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Estado de la configuración"
        intro="Lo que el planificador lee para generar un plan. Los nombres de artículos y máquinas vienen del espejo de MRPeasy."
        actions={<FreshnessBadge freshness={freshness} />}
      />

      {warnings.missingForPlanning.length > 0 && (
        <div className={`${styles['admin-page__note']} ${styles['admin-page__note--danger']}`} role="alert">
          <p>
            {'No se puede generar un plan: falta configurar '}
            {joinSpanish(warnings.missingForPlanning.map((table) => (
              <Link key={table} className={styles['admin-page__link']} to={REQUIRED[table].path}>
                {REQUIRED[table].label}
              </Link>
            )))}
            .
          </p>
        </div>
      )}

      <div className={styles['admin-page__stats']}>
        {COUNTS.map((count) => (
          <Link key={count.key} to={count.path} className={styles['admin-page__stat']}>
            <span className={styles['admin-page__stat-value']}>{counts[count.key]}</span>
            <span className={styles['admin-page__stat-label']}>{count.label}</span>
          </Link>
        ))}
      </div>

      <section className={styles['admin-page__section']}>
        <h2 className={styles['admin-page__section-title']}>Último cambio</h2>
        {lastChange === null
          ? <p className={styles['admin-page__muted']}>Nadie ha cambiado todavía la configuración desde aquí.</p>
          : (
            <p>
              {`${ENTITY_LABEL[lastChange.entity]}: ${ACTION_LABEL[lastChange.action]}, `}
              {`por ${lastChange.changedBy?.name ?? 'el sistema'} el ${formatDateTime(lastChange.changedAt)}. `}
              <Link className={styles['admin-page__link']} to={CONFIG_CHANGES_PATH}>Ver el historial</Link>
            </p>
          )}
      </section>

      <Warnings warnings={warnings} />

      <details className={styles['admin-page__details']}>
        <summary className={styles['admin-page__summary']}>
          {`Importaciones antiguas del Sheet (${sheetImports.length})`}
        </summary>
        {sheetImports.length === 0
          ? <EmptyState title="Ninguna importación" message="Esta configuración nunca se cargó desde el Sheet." />
          : <Table columns={SHEET_IMPORT_COLUMNS} rows={sheetImports} rowKey={(sync) => sync.id} caption="Importaciones del Sheet" />}
      </details>
    </section>
  )
}

/** «a, b y c», with the items as nodes (links). */
const joinSpanish = (items: Array<ReactNode>): Array<ReactNode> =>
  items.flatMap((item, index) => {
    if (index === 0) {
      return [item]
    }
    return [index === items.length - 1 ? ' y ' : ', ', item]
  })

type WarningsProps = {
  warnings: ConfigurationStatus['warnings']
}

/** Everything that deserves a look, each line pointing at the screen that fixes it. */
const Warnings = ({ warnings }: WarningsProps): ReactElement => {
  const lines: Array<{ key: string; text: string; path: string }> = []
  const codes = (values: Array<string | number>): string => values.join(', ')

  if (warnings.machinesWithoutGroup.length > 0) {
    lines.push({
      key: 'machines-without-group',
      text: `${warnings.machinesWithoutGroup.length} máquinas del espejo sin grupo: sus operaciones no se planifican `
        + `(${codes(warnings.machinesWithoutGroup.map((machine) => machine.workstationCode))}).`,
      path: CONFIG_MACHINE_GROUPS_PATH,
    })
  }
  const orphanLines: Array<[string, Array<string | number>, string, string]> = [
    ['orphan-machine-groups', warnings.orphanMachineGroups, 'Máquinas con grupo que ninguna orden reciente usa (comprueba en MRPeasy si siguen existiendo)', CONFIG_MACHINE_GROUPS_PATH],
    ['orphan-quality', warnings.orphanQualityRecipes, 'Recetas de las reglas de calidad que el espejo no conoce', CONFIG_QUALITY_RULES_PATH],
    ['orphan-special', warnings.orphanSpecialSchedule, 'Recetas de cocción baja que el espejo no conoce', CONFIG_SPECIAL_SCHEDULE_PATH],
    ['orphan-bm', warnings.orphanBmCodes, 'Códigos de homologación BM que el espejo no conoce', CONFIG_BM_HOMOLOGATIONS_PATH],
    ['orphan-groups', warnings.orphanPreproductionGroupIds, 'Grupos de preproducción que el espejo no conoce', CONFIG_SETTINGS_PATH],
    ['orphan-rice', warnings.orphanRiceCodes, 'Arroces consolidados que el espejo no conoce', CONFIG_SETTINGS_PATH],
    ['duplicated-bm', warnings.duplicatedWetacaSemiCodes, 'Semis Wetaca homologados más de una vez', CONFIG_BM_HOMOLOGATIONS_PATH],
  ]
  for (const [key, values, label, path] of orphanLines) {
    if (values.length > 0) {
      lines.push({ key, text: `${label}: ${codes(values)}.`, path })
    }
  }

  return (
    <section className={styles['admin-page__section']}>
      <h2 className={styles['admin-page__section-title']}>Avisos</h2>
      {lines.length === 0
        ? <p className={styles['admin-page__ok']}>Nada que revisar.</p>
        : (
          <ul className={styles['admin-page__list']}>
            {lines.map((line) => (
              <li key={line.key}>
                {`${line.text} `}
                <Link className={styles['admin-page__link']} to={line.path}>Revisar</Link>
              </li>
            ))}
          </ul>
        )}
    </section>
  )
}
